import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { getFeatureEntityLog } from "services/feature-apis";
import { QUERY_SUBMITTAL_TYPES } from "services/graphQL/queries";
import { useCIQMutation, useCIQQuery } from "hooks/ciq-gql-hooks";
import { useProjectParticipants } from "hooks/project-participants";
import { ProjectContext } from "context/ProjectProvider";
import { getGQL, isProjectInIntegrationMode } from "utils/utils";
import { PROJECT_INTG_CONFIGS } from "services/graphQL/ciq-queries";
import {
  getSubmittalIntegrationConfig,
  isSubmittalIntegrationMode
} from "pages/subscription-settings/integrations-tab-new/utils";
import { FeatureTypes } from "entity-app/constants";
import { DELETE_FEATURE_INSTANCE } from "entity-app/graphQL/ciq-feature-mutations";
import { gql } from "@apollo/client";
import { TAuditChangeLog } from "change-events/change-event-polling";
import { getMaxNumberOfMilestonesFromEntityList } from "entity-app/shared-components/entity-milestones-duration-columns/feature-date-block-columns";
import {
  MAX_NUMBER_OF_FETCH_ITEM,
  SubmittalStatusToStrMap
} from "../../../constants";
import {
  isSubmittalLogRestrictedView,
  SubmittalLogView
} from "../submittal-log-view";

export const DELETE_CHANGE_TYPES = new Set(["delete", "bulk_delete"]);

const hasProcessedAccImport = (
  logs: TAuditChangeLog[],
  projectId: string,
  isFileBasedIntegration: boolean
) =>
  logs.some(
    (log) =>
      log.data_source === "import_log" &&
      log.info?.import_type === "ACC_IMPORT" &&
      log.info?.status === "PROCESSED" &&
      log.project_id === projectId &&
      isFileBasedIntegration
  );

export const selectSubmittalFeatureLogs = (logs: TAuditChangeLog[]) =>
  logs.filter(
    (log) =>
      (log.info?.feature_type === "SUBMITTALS" ||
        log.info?.feature_type_ids?.includes(FeatureTypes.SUBMITTALS)) &&
      log.data_source === "feature_instance"
  );

const featureInstanceIdGroups = (logs: TAuditChangeLog[], deletions: boolean) =>
  logs
    .filter((log) => DELETE_CHANGE_TYPES.has(log.change_type) === deletions)
    .map((s: TAuditChangeLog) => s.info.feature_instance_ids) as any;

function useFeatureSubmittalLogData(
  projectId: string,
  projectToken: string,
  logView: SubmittalLogView = "default"
) {
  const isRestrictedLogView = isSubmittalLogRestrictedView(logView);
  const {
    projectDetails,
    gqlClientForProject,
    eventLogs,
    projectIntegrationConfig
  } = useContext(ProjectContext);

  // Not the project mode alone: OPC puts a project in integration mode for its
  // schedule while leaving submittals manual, and those still need the create and
  // import controls integration mode hides. See isSubmittalIntegrationMode.
  const isIntegrationMode = isSubmittalIntegrationMode(
    isProjectInIntegrationMode(projectDetails ? projectDetails.mode : 0),
    projectIntegrationConfig
  );

  const [submittalList, setSubmittalList] = useState<any>(null);
  const [submittalListLoading, setSubmittalListLoading] = useState(false);
  const [submittalListLoadedOnce, setSubmittalListLoadedOnce] = useState(false);

  const [onDataUpdateCallback, setOnDataUpdateCallback] = useState<any>(null);

  const { data: submittalTypes } = useCIQQuery(QUERY_SUBMITTAL_TYPES, {
    skip: isRestrictedLogView
  });

  const [mutationDeleteSubmittal] = useCIQMutation(
    gql(DELETE_FEATURE_INSTANCE),
    {
      client: gqlClientForProject
    }
  );

  const submittalTypesMap = useMemo(() => {
    if (submittalTypes) {
      const tmpTypesMap: any = {};
      submittalTypes.configurations_value.forEach((type: any) => {
        tmpTypesMap[type.value] = type;
      });
      return tmpTypesMap;
    }
    return null;
  }, [submittalTypes]);

  const statusesMap = useMemo(() => {
    const tmpStatusesMap: any = {};
    Object.keys(SubmittalStatusToStrMap).forEach((keyStatusId: any) => {
      tmpStatusesMap[keyStatusId] = {
        name: SubmittalStatusToStrMap[keyStatusId]
      };
    });
    return tmpStatusesMap;
  }, []);

  const { projectParticipants: WFProjectParticipants } = useProjectParticipants(
    isRestrictedLogView || isIntegrationMode
  );

  const { query: qProjectIntgConfigs } = useMemo(() => {
    return getGQL(PROJECT_INTG_CONFIGS);
  }, []);

  const { data: projectIntegConfig } = useCIQQuery(qProjectIntgConfigs, {
    client: gqlClientForProject,
    skip: !isIntegrationMode || isRestrictedLogView
  });

  const isIntegrationConfigured = !!(
    projectIntegConfig && projectIntegConfig?.project_integration_configs.length
  );

  const isFileBasedIntegration = useMemo(() => {
    // Not [0] — see getSubmittalIntegrationConfig. OPC's INTEGRATION_METHOD is
    // "api" and describes OPC, so letting it answer here hides the file-based
    // import path from a project that has one.
    const systemIntgConfig = getSubmittalIntegrationConfig(
      projectIntegrationConfig
    )?.systemIntgConfigProperties;

    return isIntegrationMode && systemIntgConfig?.INTEGRATION_METHOD === "file";
  }, [isIntegrationMode, projectIntegrationConfig]);

  const fetchLogData = useCallback(async () => {
    if (!projectToken) {
      return;
    }
    setSubmittalListLoading(true);
    const res = await getFeatureEntityLog({
      projectToken,
      featureType: FeatureTypes.SUBMITTALS,
      ...(logView === "voided" ? { void: true } : {}),
      ...(logView === "deleted" ? { deleted_from_source: true } : {})
    });
    if (res.success) {
      setSubmittalList([...res.data]);
      setSubmittalListLoadedOnce(true);
    }
    setSubmittalListLoading(false);
  }, [projectToken, logView]);

  useEffect(() => {
    if (projectId && projectToken) {
      fetchLogData();
    }
  }, [fetchLogData, projectId, projectToken]);

  const maxNumberOfMilestones = useMemo(() => {
    return getMaxNumberOfMilestonesFromEntityList(submittalList || null);
  }, [submittalList]);

  const getAndUpdateEntityList = useCallback(
    async (featureInstanceIds: Array<string>) => {
      if (!submittalList) return;

      if (featureInstanceIds.length >= MAX_NUMBER_OF_FETCH_ITEM) {
        fetchLogData();
        return;
      }

      const res = await getFeatureEntityLog({
        projectToken,
        featureType: FeatureTypes.SUBMITTALS,
        feature_instance_ids: featureInstanceIds
      });

      if (res?.success && res.data?.length) {
        const updatedList = res.data;

        const items = [...submittalList] as Array<any>;
        updatedList.forEach((item: any) => {
          const foundIndex = items.findIndex((x) => x.id === item.id);
          if (foundIndex === -1) {
            items.push(item);
          } else {
            items[foundIndex] = item;
          }
        });
        setSubmittalList(items);
      }
    },
    [submittalList, projectToken, fetchLogData]
  );

  const deleteEntitiesFromList = useCallback(
    (ids: Array<string>) => {
      const items = submittalList ? [...submittalList] : [];
      ids.forEach((id) => {
        const index = items.findIndex((x) => x.id === id);
        items.splice(index, 1);
      });
      setSubmittalList(items);
    },
    [submittalList]
  );

  const previousEventLogs = useRef(eventLogs);
  useEffect(() => {
    if (isRestrictedLogView) {
      return;
    }
    if (eventLogs?.length && previousEventLogs.current !== eventLogs) {
      if (hasProcessedAccImport(eventLogs, projectId, isFileBasedIntegration)) {
        fetchLogData();
      }

      const featureLogs = selectSubmittalFeatureLogs(eventLogs);

      if (featureLogs.length) {
        const deletedIds = featureInstanceIdGroups(featureLogs, true);
        if (deletedIds.length) {
          deleteEntitiesFromList([...new Set([].concat([], ...deletedIds))]);
        }

        // update create import
        const ids = featureInstanceIdGroups(featureLogs, false);
        if (ids.length) {
          getAndUpdateEntityList([...new Set([].concat([], ...ids))]);
        }
        onDataUpdateCallback?.();
      }
    }
    previousEventLogs.current = eventLogs;
  }, [
    deleteEntitiesFromList,
    eventLogs,
    fetchLogData,
    getAndUpdateEntityList,
    isFileBasedIntegration,
    onDataUpdateCallback,
    projectId,
    isRestrictedLogView
  ]);

  return {
    submittalList,
    maxNumberOfMilestones,
    submittalTypesMap,
    statusesMap,
    WFProjectParticipants,
    isIntegrationConfigured,
    isIntegrationMode,
    submittalListLoading,
    submittalListLoadedOnce,
    fetchLogData,
    mutationDeleteSubmittal,
    setOnDataUpdateCallback
  };
}

export default useFeatureSubmittalLogData;
